import styled from '@emotion/styled'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { apiGetFaqs } from '../../../api/faq'
import Loader from '../../../components/global/Loader'
import SearchInput from '../../../components/global/SearchInput'
import Title from '../../../components/global/Title'
import Table from '../../../components/table/Table'
import { StyledButtonLink } from '../../../components/table/Td'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { useVerifyAdminToken } from '../../../hooks/useVerifyAdminToken'
import apiClient from '../../../services/apiClient'
import { Faq } from '../../../types/faq.type'
import { AdminContainer, TitleWrapper } from '../coach/AdminCoach'
import { FaqStatusBadge } from './components/FaqStatusBadge'

const AdminFaq = () => {
    const { t } = useTranslation()
    const [searchTerm, setSearchTerm] = useState('')
    const lng = useLangSelected()
    const queryClient = useQueryClient()
    const user = useContext(UserContext)

    const deleteFaq = useCallback(
        async (id: number) => {
            try {
                await apiClient.delete(`/faq/${id}`)
                // Recharger les données après suppression
                await queryClient.invalidateQueries({ queryKey: ['faq-all'] })
                // Afficher un toast de succès
                toast.success(t('faq.deleteSuccess'))
            } catch (error) {
                console.error('Erreur lors de la suppression de la FAQ:', error)
                // Afficher un toast d'erreur
                toast.error(t('faq.deleteError'))
            }
        },
        [queryClient, t],
    )
    const verifyToken = useVerifyAdminToken()

    const { data: faqs = [], isLoading } = useQuery<Faq[]>({
        queryKey: ['faq-all'],
        queryFn: async () => {
            verifyToken()
            return await apiGetFaqs()
        },
    })

    const filteredFaqs = useMemo(
        () =>
            faqs.filter((faq: any) =>
                `${faq.question} ${faq.answer} ${faq.category}`.toLowerCase().includes(searchTerm.toLowerCase()),
            ),
        [faqs, searchTerm],
    )

    if (isLoading) return <Loader />

    return (
        <>
            <TitleWrapper>
                <Title>{t('faq.manage')}</Title>
                <LinkBtn to={`/${lng}/bva-admin/faq/creer`} className="btn btn-primary">
                    {t('common.create')}
                </LinkBtn>
            </TitleWrapper>

            <AdminContainer>
                <TitleWrapper>
                    <TableTitle>
                        {t('faq.manage')} <Badge>{filteredFaqs.length}</Badge>
                    </TableTitle>
                </TitleWrapper>
                <SearchInput
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('common.search')}
                />
                <Table
                    tableData={filteredFaqs.map((faq: any) => ({
                        id: faq.id,
                        question: faq.question,
                        category: faq.category,
                        order: faq.order,
                        status: <FaqStatusBadge isActive={faq.isActive} />,
                        edit: faq.id?.toString() ?? '',
                        remove: true,
                    }))}
                    tableHeader={[
                        '#',
                        t('faq.form.question'),
                        t('faq.form.category'),
                        t('faq.form.order'),
                        t('common.status'),
                        '',
                        '',
                    ]}
                    deleteById={(id) => deleteFaq(id)}
                />
            </AdminContainer>
        </>
    )
}

const LinkBtn = styled(StyledButtonLink)`
    display: flex;
    align-items: center;
    margin-right: 8px;
    margin-bottom: 8px;
    font-size: 1.6rem;
`

const TableTitle = styled.h2`
    font-size: 2rem;
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    gap: 1rem;
`

const Badge = styled.span`
    background-color: #007bff;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
`

export default AdminFaq
